import WidgetStyles from '../WidgetStyles';
import Accordion from '../Accordion';
import {BlockEditProps} from '@wordpress/blocks';
import {Taxonomy as Tax} from '@wordpress/api/taxonomies';
import {Fill, SelectControl} from '@wordpress/components';
import Display, {DisplayOptions} from './Display';
import DisplayPosts, {DisplayPostsOptions} from './DisplayPosts';
import Taxonomy, {TaxonomyOptions} from './Taxonomy';
import {__} from '@wordpress/i18n';
import {CONFIG} from '../../../globals/config';
import ShowCount, {ShowCountOptions} from './ShowCount';
import ExcludeField from '../ExcludeField';
import type {WithCommon} from '../navigation/block';
import {keysOf} from '../../../globals/helpers';

type BasicAttr = {
	// Attr
	exclude: string;
	new_widget: 'widget' | 'list';
	single: boolean;

	// DisplayOptions
	display_all?: boolean;
	include_childless_parent?: boolean;
	include_parent?: boolean;
	levels: number;
}

export type Attr = BasicAttr &
	{
		order: 'ASC' | 'DESC';
		order_by: string;
	}
	& DisplayOptions
	& DisplayPostsOptions
	& ShowCountOptions
	& TaxonomyOptions;

export type SetAttributes = ( newValue: {
	[attribute in keyof WithCommon<Attr>]?: WithCommon<Attr>[attribute]
} ) => void;

export type FillProps =
	Pick<BlockEditProps<WithCommon<Attr>>, 'clientId' | 'attributes' | 'setAttributes'>
	&
	{
		name: 'advanced-sidebar-menu/categories'
		type?: Tax<'edit'>
	};


type Props = Record<string, never>;

const ErrorBoundary = window.ADVANCED_SIDEBAR_MENU.ErrorBoundary;

/**
 * Categories block content in the editor.
 */
const Edit = ( {}: Props ) => {
	return ( <>
		<Fill<FillProps> name="advanced-sidebar-menu/categories/display">
			{( {attributes, setAttributes, type, name} ) => (
				<ErrorBoundary
					attributes={attributes}
					block={name}
					section={'pro/categories/Edit/display'}
				>
					<Display
						type={type}
						attributes={attributes}
						setAttributes={setAttributes} />
				</ErrorBoundary>
			)}
		</Fill>

		<Fill<FillProps> name="advanced-sidebar-menu/categories/inspector">
			{( {attributes, setAttributes, type, name} ) => (
				<ErrorBoundary
					attributes={attributes}
					block={name}
					section={'pro/categories/Edit/inspector'}
				>
					<ExcludeField
						attributes={attributes}
						setAttributes={setAttributes}
						block={name}
						type={type} />
					<Accordion
						type={type}
						attributes={attributes}
						setAttributes={setAttributes} />
					<DisplayPosts
						taxonomy={type}
						attributes={attributes}
						setAttributes={setAttributes} />
				</ErrorBoundary>
			)}
		</Fill>

		<Fill<FillProps> name="advanced-sidebar-menu/categories/block-controls">
			{( {attributes, setAttributes, name} ) => (
				<ErrorBoundary
					attributes={attributes}
					block={name}
					section={'pro/categories/Edit/block-controls'}
				>
					<WidgetStyles
						attributes={attributes}
						setAttributes={setAttributes} />
				</ErrorBoundary>
			)}
		</Fill>

		<Fill<FillProps> name="advanced-sidebar-menu/categories/general">
			{( {attributes, setAttributes, name} ) => (
				<ErrorBoundary
					attributes={attributes}
					block={name}
					section={'pro/categories/Edit/general'}
				>
					<Taxonomy
						attributes={attributes}
						setAttributes={setAttributes}
					/>
					<SelectControl
						label={__( 'Order by', 'advanced-sidebar-menu' )}
						value={attributes.order_by}
						labelPosition={'side'}
						options={Object.entries( CONFIG.categories.orderBy ).map( ( [ value, label ] ) => ( {
							value,
							label,
						} ) )}
						onChange={value => {
							setAttributes( {
								order_by: value,
							} );
						}} />
					<SelectControl<Attr['order']>
						label={__( 'Order', 'advanced-sidebar-menu' )}
						value={attributes.order}
						labelPosition={'side'}
						options={keysOf( CONFIG.categories.order ).map( key => ( {
							value: key,
							label: CONFIG.categories.order[ key ],
						} ) )}
						onChange={value => {
							setAttributes( {
								order: value,
							} );
						}} />
					<ShowCount
						attributes={attributes}
						setAttributes={setAttributes} />
				</ErrorBoundary>
			)}
		</Fill>
	</>
	);
};

export default Edit;
